/**
 * Iframe Protection Utility
 * 
 * This utility protects against malicious iframes that may be injected by
 * browser extensions, security scripts, or third-party code. It actively
 * monitors and removes iframes with suspicious properties (e.g., high z-index,
 * full-screen coverage, no visible content).
 */

// Maximum z-index threshold - any iframe above this is considered suspicious
const MAX_SAFE_Z_INDEX = 999999;

// Whitelist of legitimate iframe sources (OAuth providers, trusted services)
const LEGITIMATE_IFRAME_SOURCES = [
    'accounts.google.com',
    'accounts.google.com/gsi',
    'oauth.googleusercontent.com',
    'www.facebook.com',
    'www.facebook.com/plugins',
    'www.youtube.com',
    'player.vimeo.com'
];

// Check if iframe is from a legitimate source
const isLegitimateIframe = (iframe) => {
    const src = iframe.getAttribute('src') || '';
    if (!src) return false;

    try {
        const url = new URL(src);
        return LEGITIMATE_IFRAME_SOURCES.some(domain =>
            url.hostname === domain || url.hostname.endsWith('.' + domain)
        );
    } catch (e) {
        // If URL parsing fails, check if src contains legitimate domain
        return LEGITIMATE_IFRAME_SOURCES.some(domain => src.includes(domain));
    }
};

// Check if an iframe is suspicious
const isSuspiciousIframe = (iframe) => {
    if (!iframe || iframe.tagName !== 'IFRAME') {
        return false;
    }

    // Always allow legitimate OAuth/trusted iframes (Google Sign-In, etc.)
    if (isLegitimateIframe(iframe)) {
        return false;
    }

    const computedStyle = window.getComputedStyle(iframe);
    const zIndex = parseInt(computedStyle.zIndex, 10) || 0;

    // Check for suspicious z-index (extremely high values)
    // Note: Google uses 2147483647 for security, but we whitelist Google above
    if (zIndex > MAX_SAFE_Z_INDEX || zIndex === 2147483647) {
        return true;
    }

    // Check for full-screen coverage with fixed positioning
    const position = computedStyle.position;
    const top = computedStyle.top;
    const left = computedStyle.left;
    const width = computedStyle.width;
    const height = computedStyle.height;

    if (
        position === 'fixed' &&
        (top === '0px' || top === '0') &&
        (left === '0px' || left === '0') &&
        (width === '100%' || width === '100vw') &&
        (height === '100%' || height === '100vh')
    ) {
        // Additional check: if it has no src or a suspicious src
        const src = iframe.getAttribute('src');
        if (!src || src.trim() === '') {
            return true;
        }
    }

    return false;
};

// Remove suspicious iframe
const removeSuspiciousIframe = (iframe) => {
    try {
        if (iframe && iframe.parentNode) {
            const style = window.getComputedStyle(iframe);
            const iframeInfo = {
                zIndex: style.zIndex,
                position: style.position,
                top: style.top,
                left: style.left,
                width: style.width,
                height: style.height,
                src: iframe.getAttribute('src'),
                id: iframe.getAttribute('id'),
                className: iframe.getAttribute('class'),
                name: iframe.getAttribute('name'),
                parentElement: iframe.parentNode ? iframe.parentNode.tagName : null,
                parentId: iframe.parentNode ? iframe.parentNode.getAttribute('id') : null,
                parentClass: iframe.parentNode ? iframe.parentNode.getAttribute('class') : null,
                timestamp: new Date().toISOString(),
                stackTrace: new Error().stack
            };

            console.warn('🚨 SUSPICIOUS IFRAME DETECTED AND REMOVED:', iframeInfo);
            console.warn('📋 Full iframe element:', iframe);
            console.warn('📋 Parent element:', iframe.parentNode);
            console.warn('📋 Stack trace (where it was created):', iframeInfo.stackTrace);

            // Log all scripts that are currently loaded
            const scripts = Array.from(document.querySelectorAll('script'));
            console.warn('📜 Currently loaded scripts:', scripts.map(s => ({
                src: s.src || 'inline',
                id: s.id,
                type: s.type
            })));

            // Log all extensions that might be injecting
            if (window.chrome && window.chrome.runtime) {
                console.warn('🔌 Chrome extensions detected (if any)');
            }

            iframe.parentNode.removeChild(iframe);
            return true;
        }
    } catch (error) {
        console.error('Error removing suspicious iframe:', error);
    }
    return false;
};

// Scan and remove all suspicious iframes
export const scanAndRemoveSuspiciousIframes = () => {
    const allIframes = document.querySelectorAll('iframe');
    let removedCount = 0;

    allIframes.forEach((iframe) => {
        if (isSuspiciousIframe(iframe)) {
            if (removeSuspiciousIframe(iframe)) {
                removedCount++;
            }
        }
    });

    if (removedCount > 0) {
        console.warn(`Removed ${removedCount} suspicious iframe(s)`);
    }

    return removedCount;
};

// Initialize iframe protection
export const initializeIframeProtection = () => {
    // Immediate scan on initialization
    scanAndRemoveSuspiciousIframes();

    // Set up MutationObserver to watch for dynamically added iframes
    const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
            mutation.addedNodes.forEach((node) => {
                // Check if the added node is an iframe
                if (node.nodeType === 1 && node.tagName === 'IFRAME') {
                    if (isSuspiciousIframe(node)) {
                        removeSuspiciousIframe(node);
                    }
                }
                // Also check children of added nodes
                if (node.nodeType === 1 && node.querySelectorAll) {
                    const childIframes = node.querySelectorAll('iframe');
                    childIframes.forEach((iframe) => {
                        if (isSuspiciousIframe(iframe)) {
                            removeSuspiciousIframe(iframe);
                        }
                    });
                }
            });
        });
    });

    // Start observing the document body for changes
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });

    // Periodic scan as a backup (every 2 seconds)
    const intervalId = setInterval(() => {
        scanAndRemoveSuspiciousIframes();
    }, 2000);

    // Also scan on page visibility change (when user switches tabs back)
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            scanAndRemoveSuspiciousIframes();
        }
    });

    // Cleanup function (returned for potential cleanup if needed)
    return () => {
        observer.disconnect();
        clearInterval(intervalId);
    };
};

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeIframeProtection);
} else {
    // DOM is already ready
    initializeIframeProtection();
}

